<template>
  <div
    class="async-store-select"
    :class="{ 'opacity-50 pointer-events-none': disabled }"
    ref="wrapper"
  >
    <label
      v-if="label"
      class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
    >
      {{ label }}
    </label>
    <div class="relative">
      <vue-feather
        type="search"
        class="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400"
      />
      <input
        :value="inputValue"
        :placeholder="placeholderText"
        :disabled="disabled"
        class="w-full rounded-lg border border-gray-200 bg-white px-9 py-2 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-slate-900 dark:text-white"
        @focus="handleFocus"
        @input="handleInput"
      />
      <button
        v-if="canClear"
        type="button"
        class="absolute right-2 top-2 rounded-full p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-slate-700"
        @click="clearSelection"
      >
        <vue-feather type="x" class="h-3.5 w-3.5" />
      </button>

      <div
        v-if="showDropdown"
        class="absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-slate-800"
      >
        <div class="max-h-64 overflow-y-auto">
          <button
            v-if="includeAllOption"
            type="button"
            class="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium text-blue-600 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-slate-700"
            @click="selectAll"
          >
            <span>{{ allOptionLabel }}</span>
            <span class="text-xs text-blue-400">All</span>
          </button>

          <div v-if="loading" class="px-3 py-3 text-sm text-gray-500 dark:text-gray-300">
            Loading stores…
          </div>

          <template v-else>
            <div
              v-if="!storeResults.length"
              class="px-3 py-3 text-sm text-gray-500 dark:text-gray-300"
            >
              No stores found
            </div>
            <button
              v-for="store in storeResults"
              :key="store.id"
              type="button"
              class="flex w-full flex-col gap-0.5 px-3 py-2 text-left text-sm hover:bg-blue-50 dark:hover:bg-slate-700"
              @click="selectStore(store)"
            >
              <span class="font-medium text-gray-900 dark:text-white">{{ store.name || `Store #${store.id}` }}</span>
              <div class="text-xs text-gray-500 dark:text-gray-400">
                <span>ID: {{ store.id }}</span>
                <span v-if="store.merchant_code" class="ml-2">Code: {{ store.merchant_code }}</span>
              </div>
            </button>

            <button
              v-if="hasNext && !loading"
              type="button"
              class="flex w-full items-center justify-center gap-2 border-t border-gray-100 px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 dark:border-gray-700 dark:text-blue-300 dark:hover:bg-slate-700"
              @click.stop="loadMore"
            >
              <vue-feather type="more-horizontal" class="h-4 w-4" />
              Load more
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { toast } from "vue3-toastify";

export default {
  name: "AsyncStoreSelect",
  props: {
    modelValue: {
      type: Object,
      default: null,
    },
    includeAllOption: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: "Search stores",
    },
    label: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    allOptionLabel: {
      type: String,
      default: "All stores",
    },
    perPage: {
      type: Number,
      default: 10,
    },
  },
  emits: ["update:modelValue", "selected"],
  data() {
    return {
      searchTerm: "",
      storeResults: [],
      loading: false,
      page: 1,
      hasNext: false,
      showDropdown: false,
      isFocused: false,
      searchTimer: null,
      selectedStoreLocal: null,
    };
  },
  computed: {
    inputValue() {
      if (this.isFocused) {
        return this.searchTerm;
      }
      if (this.searchTerm) {
        return this.searchTerm;
      }
      if (this.selectedStoreLocal) {
        return this.selectedStoreLocal.name || `Store #${this.selectedStoreLocal.id}`;
      }
      return "";
    },
    placeholderText() {
      if (!this.selectedStoreLocal && this.includeAllOption) {
        return this.allOptionLabel;
      }
      return this.placeholder;
    },
    canClear() {
      return (
        this.clearable && !this.disabled && (this.selectedStoreLocal || this.searchTerm)
      );
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue) {
        const validStore =
          newValue && typeof newValue === "object" && newValue.id != null
            ? { ...newValue }
            : null;

        if (validStore) {
          this.selectedStoreLocal = validStore;
          if (!this.isFocused) {
            this.searchTerm = validStore.name || "";
          }
        } else {
          this.selectedStoreLocal = null;
          if (!this.isFocused) {
            this.searchTerm = "";
          }
        }
      },
    },
  },
  mounted() {
    this.fetchStores();
    document.addEventListener("click", this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleOutsideClick);
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
  },
  methods: {
    handleFocus() {
      if (this.disabled) {
        return;
      }
      this.isFocused = true;
      this.showDropdown = true;
      if (!this.searchTerm && this.selectedStoreLocal && this.selectedStoreLocal.id != null) {
        this.searchTerm = this.selectedStoreLocal.name || "";
      }
      this.triggerSearch();
    },
    handleInput(event) {
      if (this.disabled) {
        return;
      }
      this.searchTerm = event.target.value;
      this.showDropdown = true;
      this.triggerSearch();
    },
    triggerSearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => {
        this.fetchStores({ append: false });
      }, 250);
    },
    async fetchStores({ append = false } = {}) {
      if (this.disabled) {
        return;
      }
      try {
        if (!append) {
          this.page = 1;
        }
        this.loading = true;
        this.showDropdown = true;
        const response = await this.$store.dispatch("searchJackStores", {
          query: this.searchTerm.trim(),
          page: append ? this.page + 1 : this.page,
          perPage: this.perPage,
        });
        const items = response.items || [];
        this.page = response.page || 1;
        this.hasNext = !!response.hasNext;
        if (append) {
          this.storeResults = [...this.storeResults, ...items];
        } else {
          this.storeResults = items;
        }
      } catch (error) {
        const message =
          error?.response?.data?.error?.message ||
          "Unable to load stores. Please try again.";
        toast.error(message);
      } finally {
        this.loading = false;
      }
    },
    loadMore() {
      if (this.loading || !this.hasNext) {
        return;
      }
      this.isFocused = true;
      this.showDropdown = true;
      this.fetchStores({ append: true });
    },
    selectStore(store) {
      this.selectedStoreLocal = { ...store };
      this.searchTerm = store?.name || "";
      this.closeDropdown();
      this.$emit("update:modelValue", this.selectedStoreLocal);
      this.$emit("selected", this.selectedStoreLocal);
    },
    selectAll() {
      this.selectedStoreLocal = null;
      this.searchTerm = "";
      this.closeDropdown();
      this.$emit("update:modelValue", null);
      this.$emit("selected", null);
    },
    clearSelection() {
      if (this.includeAllOption) {
        this.selectAll();
      } else {
        this.selectedStoreLocal = null;
        this.searchTerm = "";
        this.$emit("update:modelValue", null);
        this.$emit("selected", null);
      }
    },
    closeDropdown() {
      this.showDropdown = false;
      this.isFocused = false;
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
    },
    handleOutsideClick(event) {
      const wrapper = this.$refs.wrapper;
      if (!wrapper) {
        return;
      }
      if (!wrapper.contains(event.target)) {
        this.closeDropdown();
      }
    },
  },
};
</script>

<style scoped>
.async-store-select {
  position: relative;
}
</style>
